import React, { useState, useEffect, useCallback } from 'react';
import { Chart } from 'primereact/chart';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import './css/NavLineChart.css';
import "react-toggle/style.css";
import CircularProgress from '@mui/material/CircularProgress';
import { fetchWithRetry } from './utils/api';

const NavLineChart = ({ productCode, default_index, config }) => {
  const [chartData, setChartData] = useState(null);
  const [duration, setDuration] = useState(3);
  const [numPoints, setNumPoints] = useState(50);
  const [selectedIndex, setSelectedIndex] = useState(default_index ? default_index : 'NIFTY-50');
  const [loading, setLoading] = useState(false);

  const indices = [
    { label: 'NIFTY 50', value: 'NIFTY-50' },
    { label: 'NIFTY 100', value: 'NIFTY-100' },
    { label: 'NIFTY 200', value: 'NIFTY-200' },
    { label: 'NIFTY Midcap 50', value: 'NIFTY-MIDCAP-50' },
    { label: 'NIFTY Midcap 100', value: 'NIFTY-MIDCAP-100' },
    { label: 'NIFTY Smallcap 100', value: 'NIFTY-SMALLCAP-100' },
    { label: 'NIFTY 500', value: 'NIFTY-500' },
    { label: 'NIFTY Next 50', value: 'NIFTY-NEXT-50' },
    { label: 'NIFTY Midcap 150', value: 'NIFTY-MIDCAP-150' },
    { label: 'NIFTY Smallcap 250', value: 'NIFTY-SMALLCAP-250' },
    { label: 'NIFTY 100 Liquid 15', value: 'NIFTY-100-LIQUID-15' },
    { label: 'NIFTY 50 Equal Weight', value: 'NIFTY-50-EQUAL-WEIGHT' },
    { label: 'NIFTY 100 Equal Weight', value: 'NIFTY-100-EQUAL-WEIGHT' },
    { label: 'NIFTY MidSmallcap 400', value: 'NIFTY-MID-SMALLCAP-400' },
    { label: 'NIFTY Alpha 50', value: 'NIFTY-ALPHA-50' },
    { label: 'NIFTY 50 Value 20', value: 'NIFTY-50-VALUE-20' },
    { label: 'NIFTY Quality 30', value: 'NIFTY-QUALITY-30' },
    { label: 'NIFTY Low Volatility 50', value: 'NIFTY-LOWVOL-50' },
    { label: 'NIFTY Momentum 30', value: 'NIFTY-MOMENTUM-30' },
    { label: 'NIFTY 50 Dividend Points', value: 'NIFTY-50-DIVPOINTS' },
    { label: 'NIFTY 100 Quality 30', value: 'NIFTY-100-QUALITY-30' },
    { label: 'NIFTY 200 Momentum 30', value: 'NIFTY-200-MOMENTUM-30' },
    { label: 'NIFTY 500 Value 50', value: 'NIFTY-500-VALUE-50' }
  ];

  useEffect(() => {
    fetchData();
  }, [duration, selectedIndex, productCode]);

  const handleDurationChange = (newDuration) => {
    setDuration(newDuration);
  };

  const fetchData = async () => {
    try {
      const navApiUrl = `${config.PY_API_URL}product/${productCode}/returns?product_code=${productCode}&duration=${duration}&num_points=${numPoints}`;
      const returnsApiUrl = `${config.PY_API_URL}/index/${selectedIndex}/returns?index_name=${selectedIndex}&duration=${duration}&num_points=${numPoints}`;
      
      setLoading(true);
      setChartData(null);

      const [navData, returnsData] = await Promise.allSettled([
        fetchWithRetry(navApiUrl),
        fetchWithRetry(returnsApiUrl)
      ]);

      let chartDataSet = [];

      // Process NAV data if available
      if (navData.status === 'fulfilled' && navData.value?.data) {
        const navPoints = navData.value.data.data;
        const labels = navPoints.map(point => new Date(point.Date).toLocaleDateString());
        const navValues = navPoints.map(point => point.nav);

        chartDataSet.push({
          label: 'This Fund',
          data: navValues,
          borderColor: '#66BB6A',
          fill: false,
          borderWidth: 2,
          pointRadius: 0.44,
          pointHoverRadius: 2,
          yAxisID: 'left-y-axis',
        });

        setChartData(prevData => ({
          ...prevData,
          labels,
        }));
      }

      // Process Returns data if available
      if (returnsData.status === 'fulfilled' && returnsData.value?.data) {
        const returnPoints = returnsData.value.data.data;
        const returnValues = returnPoints.map(point => point.nav);

        chartDataSet.push({
          label: `${selectedIndex}`,
          data: returnValues,
          fill: false,
          borderColor: '#42A5F5',
          borderWidth: 2,
          pointRadius: 0.44,
          pointHoverRadius: 0.6,
          yAxisID: 'right-y-axis',
        });

        // Set labels from returns data if NAV data wasn't available
        if (!navData.value?.data) {
          const labels = returnPoints.map(point => new Date(point.Date).toLocaleDateString());
          setChartData(prevData => ({
            ...prevData,
            labels,
          }));
        }
      }

      // Update chart data if we have any datasets
      if (chartDataSet.length > 0) {
        setChartData(prevData => ({
          ...prevData,
          datasets: chartDataSet,
        }));
      } else {
        setChartData(null);
      }

    } catch (error) {
      console.error('Failed to fetch NAV or Returns data:', error);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };
  
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  
  const chartOptions = {
      interaction: {
        mode: 'index',  // Ensures hover triggers across the entire x-axis
        intersect: false // Shows tooltip without direct intersection with a point
      },
      plugins: {
          legend: {
              labels: {
                  color: textColor
              }
          },
          tooltips: {
            mode: 'index',        // Combine tooltips for both lines
            intersect: false,     // Show tooltip even if not directly hovering over a point
          },
      },
      
      scales: {
          x: {
              // type: 'time',
              // time: {
              //   unit: 'month',  // Set the unit to 'month'
              //   displayFormats: {
              //     month: 'MMM-yyyy' // Customize display to show Month-Year (e.g., Aug-2024)
              //   }
              // },
              ticks: {
                maxTicksLimit: 12,  // Set the maximum number of labels
                  // color: textColorSecondary,
                  // display: false // Hides x-axis labels
              },
              grid: {
                // display: false // Hides vertical grid lines
                  // color: surfaceBorder
              }
          },
          'left-y-axis': {
              type: 'linear',
              position: 'left',
              ticks: {
                  callback: function(value) {
                      return value + ' ₹';
                  },
              },
          },
          'right-y-axis': {
              type: 'linear',
              position: 'right',
              ticks: {
                  callback: function(value) {
                      return value + ' ₹';
                  },
              },
              grid: {
                  drawOnChartArea: false, // Only grid lines for one axis
              },
          },
      }
  };

  return (
    <div className="return-chart-section">
      <div className="chart-head-block">
        <div className='chart-head-left'>
          <h4>Return Chart</h4>
        </div>
        <div className='chart-head-right'>
          <div className="duration-buttons line-chart-buttons">
            <span className={duration === 1 ? 'chart-age-btn active' : 'chart-age-btn'} onClick={() => handleDurationChange(1)}>1Y</span>
            <span className={duration === 3 ? 'chart-age-btn active' : 'chart-age-btn'} onClick={() => handleDurationChange(3)}>3Y</span>
            <span className={duration === 5 ? 'chart-age-btn active' : 'chart-age-btn'} onClick={() => handleDurationChange(5)}>5Y</span>
            {/* <button className={duration === 'lifetime' ? 'active' : ''} onClick={() => handleDurationChange('lifetime')}></button> */}
          </div>
          <div className="index-dropdown">
            <select value={selectedIndex} onChange={(e) => setSelectedIndex(e.target.value)}>
              {indices.map((index) => (
                <option key={index.value} value={index.value}>{index.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="return-info">
        <h3>{
          // returns ? `${returns}% ${duration}Y Annualised` : 'Loading...'
        }</h3>
      </div>
      {loading && <CircularProgress />}
      {chartData && (
        <Chart
          style={{ width: "100%", minHeight: "300px" }}
          type="line"
          data={chartData}
          options={chartOptions}
          options1={{
            responsive: true,
            maintainAspectRatio: false,
            tooltips: {
              callbacks: {
                label: function (tooltipItem, data) {
                  let dataset = data.datasets[tooltipItem.datasetIndex];
                  let datasetLabel = dataset.label || '';
                  let value = dataset.data[tooltipItem.index];
                  return datasetLabel + ': ' + value;
                }
              }
            },
            scales: {
              xAxes: [{
                type: 'time',
                time: {
                  unit: 'month'
                },
                distribution: 'linear'
              }]
            }
          }}
        />
      )}
      
    </div>
  );
};

export default NavLineChart;
